import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { CartDrawer } from './cart/CartDrawer';
import { CartPage } from './cart/CartPage';
import { OrdersPage } from './orders/OrdersPage';
import { EnsureShopwareLogin } from './common/EnsureShopwareLogin';
import { ShopwareProvider, useShopware } from './shopware/ShopwareContext';
import { createPortal } from 'preact/compat';
import { ProductInfo } from '~/components-shop/product-info/ProductInfo';
import { useJsonProvider } from './JsonProvider';
import { ShopwareLabTest } from './shopware/ShopwareLabTest';
import { AddToCartIsland } from './add-to-cart/AddToCartIsland';
import { ROOT_SELECTOR as ADD_TO_CART_ROOT_SELECTOR } from './add-to-cart/addToCartData';

const ShopAppContent = () => {
  const { isDrawerOpened, closeDrawer } = useShopware();
  const { dosageForms } = useJsonProvider();
  const [productInfoPortalTarget, setProductInfoPortalTarget] = useState<HTMLElement | null>(null);
  const [originalProductInfoHTML, setOriginalProductInfoHTML] = useState<string>('');
  const [cartPagePortalTarget, setCartPagePortalTarget] = useState<HTMLElement | null>(null);
  const [ordersPagePortalTarget, setOrdersPagePortalTarget] = useState<HTMLElement | null>(null);
  // ORT-3057: add-to-cart islands, one per brick instance (Landingpage Hero, later ORT-3055 product bricks)
  const [addToCartRoots, setAddToCartRoots] = useState<HTMLElement[]>([]);

  // Find portal targets after component mounts and clear skeleton content
  useEffect(() => {
    // Product Info Portal
    const productInfoTarget = document.getElementById('preact-product-infos');
    if (productInfoTarget) {
      setOriginalProductInfoHTML(productInfoTarget.innerHTML);
      productInfoTarget.innerHTML = '';
    }
    setProductInfoPortalTarget(productInfoTarget);

    // Cart Page Portal
    const cartPageTarget = document.getElementById('preact-cart-page');
    if (cartPageTarget) {
      cartPageTarget.innerHTML = '';
      cartPageTarget.setAttribute('aria-busy', 'false');
    }
    setCartPagePortalTarget(cartPageTarget);

    // Orders Page Portal
    const ordersPageTarget = document.getElementById('preact-orders-page');
    if (ordersPageTarget) {
      ordersPageTarget.innerHTML = '';
      ordersPageTarget.setAttribute('aria-busy', 'false');
    }
    setOrdersPagePortalTarget(ordersPageTarget);

    // Add-to-cart islands: every root marks one brick instance, the island finds its slots inside it
    setAddToCartRoots(Array.from(document.querySelectorAll<HTMLElement>(ADD_TO_CART_ROOT_SELECTOR)));
  }, []);

  const hasDosageForms =
    dosageForms.data &&
    Array.isArray(dosageForms.data.dosageForms) &&
    dosageForms.data.dosageForms.length > 0 &&
    !dosageForms.error;

  return (
    <>
      {productInfoPortalTarget &&
        hasDosageForms &&
        createPortal(<ProductInfo originalInnerHTML={originalProductInfoHTML} />, productInfoPortalTarget)}
      {cartPagePortalTarget && createPortal(<CartPage />, cartPagePortalTarget)}
      {ordersPagePortalTarget && createPortal(<OrdersPage />, ordersPagePortalTarget)}
      {addToCartRoots.map((root, index) => (
        <AddToCartIsland key={index} root={root} />
      ))}
      {/*
        Cart drawer at page level so it can open from the PDP and from any add-to-cart island.
        Its styles are scoped under .e-shop (formerly inherited from the PDP's keyvisual wrapper).
      */}
      {(productInfoPortalTarget || addToCartRoots.length > 0) && (
        <div className="e-shop">
          <CartDrawer isOpen={isDrawerOpened} onClose={closeDrawer} />
        </div>
      )}
      {/*
        Mounted unconditionally on every page where ShopApp renders. The hook
        short-circuits cheaply when there's nothing to do (no SSO cookie), and
        re-validates on bfcache restore via its own pageshow listener — gating
        on the cookie ourselves was a one-shot mount snapshot that missed
        bfcache restores and cross-tab logins.
      */}
      <EnsureShopwareLogin />

      {/* <ShopwareLabTest /> */}
    </>
  );
};

export const ShopApp = () => {
  return (
    <ShopwareProvider>
      <ShopAppContent />
    </ShopwareProvider>
  );
};
